import { useEffect, useState } from 'react';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import useOtherUserProfile from '@wlb/shared/hooks/useOtherUserProfile';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { Profile } from '@wlb/shared/types/profile';
import { normalizeUserGuid } from '@wlb/shared/utils/navigateToUserProfile';
import {
  isFollowRelationshipTarget,
  notifyFollowRelationshipChanged,
  subscribeToFollowRelationshipChanges,
} from '@wlb/shared/utils/followRelationshipEvents';
import { useAuthGate } from '../../components/feedback/AuthGate';
import { PlusIcon } from '../../components/ui/icons';
import { isAiAgentProfileGuid } from './aiAgentIdentity';
import './feed.css';

export type ReshareAuthorFollowState = 'FOLLOW' | 'FOLLOWING' | 'HIDDEN';

interface ReshareFollowCacheEntry {
  userId: number;
  isFollowing: boolean;
  agentStatus: 'agent' | 'member';
  savedAt: number;
}

const RESHARE_FOLLOW_CACHE_PREFIX = 'wlb.reshare-follow';
const RESHARE_FOLLOW_CACHE_MAX_AGE = 6 * 60 * 60 * 1000;

export const readReshareFollowCache = (
  storage: Pick<Storage, 'getItem'> | null,
  key: string,
  now = Date.now(),
): ReshareFollowCacheEntry | null => {
  if (!storage || !key) return null;
  try {
    const parsed: unknown = JSON.parse(storage.getItem(key) ?? '');
    if (!parsed || typeof parsed !== 'object') return null;
    const entry = parsed as Partial<ReshareFollowCacheEntry>;
    if (
      !Number.isSafeInteger(entry.userId)
      || Number(entry.userId) <= 0
      || typeof entry.isFollowing !== 'boolean'
      || (entry.agentStatus !== 'agent' && entry.agentStatus !== 'member')
      || typeof entry.savedAt !== 'number'
      || now - entry.savedAt > RESHARE_FOLLOW_CACHE_MAX_AGE
    ) {
      return null;
    }
    return entry as ReshareFollowCacheEntry;
  } catch {
    return null;
  }
};

export const getReshareAuthorFollowState = (
  profile: Profile | null,
  currentUserGuid?: string | null,
  originalAuthorGuid?: string | null,
  followingOverride: boolean | null = null,
  isAiAgent = false,
): ReshareAuthorFollowState => {
  const viewerGuid = normalizeUserGuid(currentUserGuid)?.toLowerCase();
  const authorGuid = normalizeUserGuid(originalAuthorGuid)?.toLowerCase();
  if (
    isAiAgent
    || !profile?.userId
    || (viewerGuid && viewerGuid === authorGuid)
  ) {
    return 'HIDDEN';
  }

  return (followingOverride ?? profile.isFollowing) ? 'FOLLOWING' : 'FOLLOW';
};

interface ReshareAuthorFollowActionProps {
  originalAuthorGuid: string;
  currentUserGuid?: string | null;
}

export function ReshareAuthorFollowAction({
  originalAuthorGuid,
  currentUserGuid,
}: ReshareAuthorFollowActionProps) {
  const { t } = useTranslation();
  const { requireAuth } = useAuthGate();
  const { profile: viewerProfile, userRelation } = useProfile();
  const viewerGuid = normalizeUserGuid(currentUserGuid)?.toLowerCase();
  const authorGuid = normalizeUserGuid(originalAuthorGuid)?.toLowerCase();
  const cacheKey = viewerGuid && authorGuid
    ? `${RESHARE_FOLLOW_CACHE_PREFIX}:${viewerGuid}:${authorGuid}`
    : '';
  const getCachedRelation = () => {
    try {
      return readReshareFollowCache(window.sessionStorage, cacheKey);
    } catch {
      return null;
    }
  };
  const [cachedRelation, setCachedRelation] = useState<ReshareFollowCacheEntry | null>(
    getCachedRelation,
  );
  const profileTargetGuid =
    viewerGuid && viewerGuid === authorGuid ? null : originalAuthorGuid;
  const { profile, loading, error } = useOtherUserProfile(profileTargetGuid);
  const [updating, setUpdating] = useState(false);
  const [followingOverride, setFollowingOverride] = useState<boolean | null>(null);
  const followedAgentGuid = normalizeUserGuid(
    viewerProfile?.followedAiAgent?.agentGuid,
  )?.toLowerCase();
  const [agentStatus, setAgentStatus] = useState<
    'checking' | 'agent' | 'member'
  >(
    followedAgentGuid && followedAgentGuid === authorGuid
      ? 'agent'
      : cachedRelation?.agentStatus ?? 'checking',
  );

  useEffect(() => {
    const cached = getCachedRelation();
    setCachedRelation(cached);
    setFollowingOverride(null);
    setAgentStatus(
      followedAgentGuid && followedAgentGuid === authorGuid
        ? 'agent'
        : cached?.agentStatus ?? 'checking',
    );
  // `cacheKey` includes every identity this reset depends on.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  useEffect(() => {
    if (followedAgentGuid && followedAgentGuid === authorGuid) {
      setAgentStatus('agent');
      return;
    }
    if (cachedRelation) {
      setAgentStatus(cachedRelation.agentStatus);
      return;
    }
    // A confirmed human Follow relationship is enough to render immediately.
    // Do not hold an existing follower behind the slower paginated AI roster.
    if (profile?.isFollowing === true) {
      setAgentStatus('member');
      return;
    }

    let cancelled = false;
    setAgentStatus('checking');
    isAiAgentProfileGuid(originalAuthorGuid)
      .then(isAgent => {
        if (!cancelled) setAgentStatus(isAgent ? 'agent' : 'member');
      })
      .catch(() => {
        // Fail closed: an AI agent must use the Agent relationship, never the
        // human Follow relationship.
        if (!cancelled) setAgentStatus('checking');
      });
    return () => {
      cancelled = true;
    };
  }, [
    authorGuid,
    cachedRelation,
    followedAgentGuid,
    originalAuthorGuid,
    profile?.isFollowing,
  ]);

  const effectiveProfile = profile ?? (cachedRelation
    ? {
        userId: cachedRelation.userId,
        isFollowing: cachedRelation.isFollowing,
      } as Profile
    : null);

  const persistRelation = (
    isFollowing: boolean,
    status: 'agent' | 'member',
  ) => {
    const userId = Number(effectiveProfile?.userId);
    if (!cacheKey || !Number.isSafeInteger(userId) || userId <= 0) return;
    const entry: ReshareFollowCacheEntry = {
      userId,
      isFollowing,
      agentStatus: status,
      savedAt: Date.now(),
    };
    setCachedRelation(entry);
    try {
      window.sessionStorage.setItem(cacheKey, JSON.stringify(entry));
    } catch {
      // Restricted storage still gets the in-memory optimistic state.
    }
  };

  useEffect(() => {
    if (!profile?.userId || agentStatus === 'checking') return;
    persistRelation(Boolean(profile.isFollowing), agentStatus);
  // Persist only when fresh server relationship data resolves.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentStatus, profile?.isFollowing, profile?.userId]);

  useEffect(() => {
    const subscription = subscribeToFollowRelationshipChanges(change => {
      if (!isFollowRelationshipTarget(change, {
        userId: effectiveProfile?.userId,
        profileUserGuid: originalAuthorGuid,
      })) {
        return;
      }

      setFollowingOverride(change.isFollowing);
      const userId = Number(effectiveProfile?.userId || change.relatedUserId);
      if (!cacheKey || !Number.isSafeInteger(userId) || userId <= 0) return;
      const entry: ReshareFollowCacheEntry = {
        userId,
        isFollowing: change.isFollowing,
        agentStatus: 'member',
        savedAt: Date.now(),
      };
      setCachedRelation(entry);
      try {
        window.sessionStorage.setItem(cacheKey, JSON.stringify(entry));
      } catch {
        // Restricted storage still gets the in-memory relationship event.
      }
    });

    return () => subscription.remove();
  }, [cacheKey, effectiveProfile?.userId, originalAuthorGuid]);

  if (error && !cachedRelation) return null;
  if ((loading && !cachedRelation) || agentStatus === 'checking') {
    return (
      <span
        className="post-card__reshared-follow-loading"
        role="status"
        aria-label={t('common.loading')}
      />
    );
  }

  const state = getReshareAuthorFollowState(
    effectiveProfile,
    currentUserGuid,
    originalAuthorGuid,
    followingOverride,
    agentStatus === 'agent',
  );
  if (state === 'HIDDEN') return null;

  const updateFollow = async () => {
    const relatedUserId = Number(effectiveProfile?.userId);
    if (!relatedUserId || updating) return;

    const wasFollowing = state === 'FOLLOWING';
    const nextFollowing = !wasFollowing;
    setFollowingOverride(nextFollowing);
    setUpdating(true);
    try {
      const result = await userRelation(
        relatedUserId,
        wasFollowing ? 'UnFollow' : 'Follow',
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Follow relation update failed');
      }
      const confirmedFollowing =
        typeof result?.isFollowing === 'boolean'
          ? result.isFollowing
          : nextFollowing;
      setFollowingOverride(confirmedFollowing);
      persistRelation(confirmedFollowing, 'member');
      notifyFollowRelationshipChanged({
        relatedUserId,
        profileUserGuid: originalAuthorGuid,
        isFollowing: confirmedFollowing,
      });
      ToastService.show({
        type: 'success',
        message: t(
          nextFollowing
            ? 'profile.toast.followed'
            : 'profile.toast.unfollowed',
        ),
      });
    } catch {
      setFollowingOverride(wasFollowing);
      ToastService.show({
        type: 'error',
        message: t('common.errors.generic'),
      });
    } finally {
      setUpdating(false);
    }
  };

  const following = state === 'FOLLOWING';
  const label = t(
    updating
      ? 'common.loading'
      : following
        ? 'likes.following'
        : 'profile.buttons.follow',
  );

  return (
    <button
      className={`post-card__reshared-follow${
        following ? ' post-card__reshared-follow--following' : ''
      }`}
      type="button"
      disabled={updating}
      aria-busy={updating}
      aria-pressed={following}
      onClick={() => requireAuth(() => {
        updateFollow().catch(() => undefined);
      })}
    >
      {!following && !updating && <PlusIcon size={16} />}
      {label}
    </button>
  );
}
